<template>
  <VContainer class="flex flex-col gap-2">
    <div class="flex justify-between items-center">
      <h2 data-testid="consultation-panel-heading" class="text-h3 text-sm-h2 max-w-[60%]">
        {{ displayHeading }}
      </h2>

      <VBtn
        v-if="canCreateConsultations"
        data-testid="menu-request-button"
        :to="{ name: routeNames.NEW_CONSULTATION }"
        :active="false"
        :disabled="disableConsultationButton"
        class="button-large max-xl:gap-0"
        color="primary"
      >
        <span>
          {{ t('Create consultation') }}
        </span>
      </VBtn>
    </div>

    <div class="flex gap-2 items-center">
      <ConsultationFilterButton />
      <ConsultationFilterChips />
    </div>

    <SentAndReceivedConsultationTabs
      :received-direct-consultations="filteredIncomingDirectConsultations"
      :sent-direct-consultations="filteredOutgoingDirectConsultations"
      :received-scheduled-consultations="filteredIncomingScheduledConsultations"
      :sent-scheduled-consultations="filteredOutgoingScheduledConsultations"
      :initial-tab="initialTab"
    />
  </VContainer>
</template>

<script setup lang="ts">
import SentAndReceivedConsultationTabs from '@/components/consultationrequest/SentAndReceivedConsultationTabs.vue'
import { useConsultationRequestStore } from '@/stores/consultationRequest'
import { useUserStore } from '@/stores/user'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { routeNames } from '@/router/routeNames'
import ConsultationFilterButton from './filter/ConsultationFilterButton.vue'
import ConsultationFilterChips from './filter/ConsultationFilterChips.vue'
import type { ConsultationRequestDTO, ScheduledConsultationDTO } from '@/generated'
import {
  filterDirectConsultationRequests,
  filterScheduledConsultationRequests
} from '@/util/consultationFilters'
import type { ConsultationsViewTab } from '@/util/consultationsTab'
const { t } = useI18n()

const { initialTab = 'received' } = defineProps<{
  initialTab?: ConsultationsViewTab
}>()

const { hasChiefPhysicianShift, hasGodModeShift, canCreateConsultations } =
  storeToRefs(useUserStore())
const consultationRequestStore = useConsultationRequestStore()
const {
  incomingDirectConsultations,
  outgoingDirectConsultations,
  outgoingScheduledConsultations,
  incomingScheduledConsultations,
  consultationFilter
} = storeToRefs(consultationRequestStore)
const disableConsultationButton = computed(() => hasGodModeShift.value)

const consultationCount = computed(() => {
  return [
    incomingDirectConsultations.value.length,
    outgoingDirectConsultations.value.length,
    incomingScheduledConsultations.value.length,
    outgoingScheduledConsultations.value.length
  ].reduce((sum, curr) => sum + curr, 0)
})
const displayHeading = computed<string>(() => {
  return `${t('Consultations')} (${consultationCount.value})`
})

const filteredIncomingDirectConsultations = computed<ConsultationRequestDTO[]>(() => {
  return incomingDirectConsultations.value.filter((consultation) =>
    filterDirectConsultationRequests(
      consultation,
      consultationFilter.value,
      hasChiefPhysicianShift.value
    )
  )
})
const filteredOutgoingDirectConsultations = computed<ConsultationRequestDTO[]>(() => {
  return outgoingDirectConsultations.value.filter((consultation) =>
    filterDirectConsultationRequests(
      consultation,
      consultationFilter.value,
      hasChiefPhysicianShift.value
    )
  )
})
const filteredIncomingScheduledConsultations = computed<ScheduledConsultationDTO[]>(() => {
  return incomingScheduledConsultations.value.filter((consultation) =>
    filterScheduledConsultationRequests(
      consultation,
      consultationFilter.value,
      hasChiefPhysicianShift.value
    )
  )
})
const filteredOutgoingScheduledConsultations = computed<ScheduledConsultationDTO[]>(() => {
  return outgoingScheduledConsultations.value.filter((consultation) =>
    filterScheduledConsultationRequests(
      consultation,
      consultationFilter.value,
      hasChiefPhysicianShift.value
    )
  )
})
</script>

<style scoped>
.v-expansion-panel-title :hover {
  background-color: transparent !important;
}
</style>
